/**
 * The "Overview" tab: the facts about the account, the connected sign-in providers, and the one
 * corrective action that is neither access nor billing — awarding a check-in that really happened.
 *
 * Award Check-In sits here rather than under Subscription because it has nothing to do with a
 * plan: it writes a completion so achievement and collection progress move as they would have at
 * the time.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material'
import {
  Badge,
  CalendarMonth,
  Email,
  EmojiEvents as EmojiEventsIcon,
  Link as LinkIcon,
  Login,
  Person,
  Phone,
  VolumeOff,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@api/endpoints/admin'
import type { AdminUserDto } from '@api/types/admin'
import { UserFlags } from '@api/types/auth'
import { hasUserFlag } from '@utils/userFlags'
import AwardCheckInDialog from '../AwardCheckInDialog'
import AwardRankedCompletionDialog from '../AwardRankedCompletionDialog'
import { adminUserConnectionsKey } from './userQueryKeys'
import { useIsAdmin } from './useIsAdmin'
import { errorMessage, formatDate, userLabel } from './userFormatting'

export interface UserOverviewPanelProps {
  user: AdminUserDto
}

export function UserOverviewPanel({ user }: UserOverviewPanelProps) {
  // Moderators see the facts only: awarding writes completions, which is Admin-only.
  const isAdmin = useIsAdmin()
  const [awardOpen, setAwardOpen] = useState(false)
  const [rankedAwardOpen, setRankedAwardOpen] = useState(false)

  // One cheap call — the endpoint returns the OAuth rows for this user and nothing else.
  const {
    data: connections,
    isLoading: connectionsLoading,
    error: connectionsError,
  } = useQuery({
    queryKey: adminUserConnectionsKey(user.id),
    queryFn: () => adminApi.getUserConnections(user.id),
  })

  const connectionRows = Array.isArray(connections) ? connections : []

  return (
    <Box>
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <Email />
            </ListItemIcon>
            <ListItemText
              primary="Email"
              secondary={user.email}
              secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
            />
          </ListItem>
          {user.firstName && (
            <ListItem>
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText primary="Name" secondary={`${user.firstName} ${user.lastName || ''}`} />
            </ListItem>
          )}
          {user.phoneNumber && (
            <ListItem>
              <ListItemIcon>
                <Phone />
              </ListItemIcon>
              <ListItemText primary="Phone" secondary={user.phoneNumber} />
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <CalendarMonth />
            </ListItemIcon>
            <ListItemText primary="Joined" secondary={formatDate(user.createdAt)} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Login />
            </ListItemIcon>
            <ListItemText primary="Last Login" secondary={formatDate(user.lastLoginAt)} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <VolumeOff />
            </ListItemIcon>
            <ListItemText
              primary="Can create silent adventures"
              secondary={
                hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)
                  ? isAdmin
                    ? 'Yes — change it under Access'
                    : 'Yes'
                  : 'No'
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Badge />
            </ListItemIcon>
            <ListItemText
              primary="User ID"
              secondary={user.id}
              primaryTypographyProps={{ variant: 'body2' }}
              // GUIDs are wider than a phone; break them rather than let one widen the page.
              secondaryTypographyProps={{ variant: 'caption', sx: { wordBreak: 'break-all' } }}
            />
          </ListItem>
        </List>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon fontSize="small" /> Connected Accounts
        </Typography>
        {connectionsError ? (
          <Alert severity="error">{errorMessage(connectionsError, 'Failed to load connections')}</Alert>
        ) : connectionsLoading ? (
          <CircularProgress size={20} />
        ) : connectionRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No connected accounts
          </Typography>
        ) : (
          <List dense disablePadding>
            {connectionRows.map((conn) => (
              <ListItem key={conn.id} disableGutters>
                <ListItemText
                  primary={conn.provider}
                  secondary={conn.providerEmail || conn.providerUsername || conn.providerUserId}
                  secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {isAdmin && (
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEventsIcon fontSize="small" /> Achievements
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Record a visit that happened but was never detected, or give a run that could not be verified
          a ranked time and steps. Achievement and collection progress move as they would have at the time.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmojiEventsIcon />}
            onClick={() => setAwardOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Award Check-In
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmojiEventsIcon />}
            onClick={() => setRankedAwardOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Award Ranked Completion
          </Button>
        </Box>
      </Paper>
      )}

      {isAdmin && (
      <>
      <AwardCheckInDialog
        open={awardOpen}
        onClose={() => setAwardOpen(false)}
        userId={user.id}
        userLabel={userLabel(user)}
      />
      <AwardRankedCompletionDialog
        open={rankedAwardOpen}
        onClose={() => setRankedAwardOpen(false)}
        userId={user.id}
        userLabel={userLabel(user)}
      />
      </>
      )}
    </Box>
  )
}
